import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ScrollText, Undo2, AlertTriangle, Check, X } from 'lucide-react';
import {
  ACTION_LABELS, ACTION_CLASS, computeChanges, entrySummaryFields, formatValue, formatDateTime,
} from '../../utils/auditDiff';
import './AuditLog.css';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  return fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: 'include',
  });
}

const ACTIONS = ['create', 'update', 'delete', 'revert'];

// Reusable action badge — shared with AuditHistoryModal.
export function ActionBadge({ action }) {
  return (
    <span className={`al-badge ${ACTION_CLASS[action] || ''}`}>
      {ACTION_LABELS[action] || action}
    </span>
  );
}

// Reusable diff renderer — shared with AuditHistoryModal.
// `reverse` shows the change in the undo direction (new → old).
export function ChangeSummary({ entry, reverse = false, maxFields = 4 }) {
  if (entry.action === 'delete' && !reverse) {
    return <span className="al-change-note">verwijderd</span>;
  }
  if (entry.action === 'create' && !reverse) {
    const fields = entrySummaryFields(entry.new_values, 3);
    return (
      <span className="al-change">
        <span className="al-change-note">nieuw aangemaakt</span>
        {fields.map((f) => (
          <span className="al-change-row" key={f.field}>
            <span className="al-field">{f.field}:</span>
            <span className="al-new">{f.value}</span>
          </span>
        ))}
      </span>
    );
  }

  // update / revert (or reversed preview of create/delete)
  let changes = computeChanges(entry.old_values, entry.new_values);
  if (reverse) changes = changes.map((c) => ({ field: c.field, from: c.to, to: c.from }));

  if (changes.length === 0) {
    if (entry.action === 'create') return <span className="al-change-note">aanmaak ongedaan maken</span>;
    if (entry.action === 'delete') return <span className="al-change-note">herstellen</span>;
    return <span className="al-change-note">geen veldwijzigingen</span>;
  }

  const shown = changes.slice(0, maxFields);
  const extra = changes.length - shown.length;
  return (
    <span className="al-change">
      {shown.map((c) => (
        <span className="al-change-row" key={c.field}>
          <span className="al-field">{c.field}:</span>
          <span className="al-old">{formatValue(c.from)}</span>
          <span className="al-arrow">→</span>
          <span className="al-new">{formatValue(c.to)}</span>
        </span>
      ))}
      {extra > 0 && <span className="al-more">+{extra} meer</span>}
    </span>
  );
}

export default function AuditLog() {
  const [entries, setEntries] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ entity_type: '', user_id: '', action: '', q: '' });
  const [confirm, setConfirm] = useState(null); // entry pending revert
  const [reverting, setReverting] = useState(false);
  const [revertError, setRevertError] = useState('');
  const [toast, setToast] = useState('');

  const loadEntities = useCallback(async () => {
    try {
      const res = await authFetch('/api/audit-logs/entities');
      const data = await res.json();
      setEntityTypes(Array.isArray(data?.entityTypes) ? data.entityTypes : []);
      setUsers(Array.isArray(data?.users) ? data.users : []);
    } catch (err) { console.error('load audit entities', err); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.entity_type) params.set('entity_type', filters.entity_type);
      if (filters.user_id) params.set('user_id', filters.user_id);
      if (filters.action) params.set('action', filters.action);
      if (filters.q) params.set('q', filters.q);
      params.set('limit', '100');
      const res = await authFetch(`/api/audit-logs?${params.toString()}`);
      const data = await res.json();
      setEntries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('load audit logs', err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadEntities(); }, [loadEntities]);
  useEffect(() => { load(); }, [load]);

  const setFilter = (key, val) => setFilters((f) => ({ ...f, [key]: val }));

  const doRevert = async () => {
    if (!confirm) return;
    setReverting(true);
    setRevertError('');
    try {
      const res = await authFetch(`/api/audit-logs/${confirm.id}/revert`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.error) {
        setRevertError(data?.error || 'Terugdraaien mislukt.');
        return;
      }
      setConfirm(null);
      setToast('Wijziging teruggedraaid.');
      setTimeout(() => setToast(''), 3000);
      load();
    } catch (err) {
      console.error('revert', err);
      setRevertError('Terugdraaien mislukt.');
    } finally {
      setReverting(false);
    }
  };

  return (
    <div className="audit-log">
      <div className="al-header">
        <div>
          <h2><ScrollText size={22} /> Logboek</h2>
          <p className="al-sub">Overzicht van alle wijzigingen in het systeem</p>
        </div>
        <button className="al-refresh" onClick={load} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'spin' : ''} />
          Vernieuwen
        </button>
      </div>

      <div className="al-filters">
        <select value={filters.entity_type} onChange={(e) => setFilter('entity_type', e.target.value)}>
          <option value="">Alle onderdelen</option>
          {entityTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filters.user_id} onChange={(e) => setFilter('user_id', e.target.value)}>
          <option value="">Alle accounts</option>
          {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.user_email}</option>)}
        </select>
        <select value={filters.action} onChange={(e) => setFilter('action', e.target.value)}>
          <option value="">Alle acties</option>
          {ACTIONS.map((a) => <option key={a} value={a}>{ACTION_LABELS[a]}</option>)}
        </select>
        <input
          type="text"
          placeholder="Zoeken…"
          value={filters.q}
          onChange={(e) => setFilter('q', e.target.value)}
        />
      </div>

      <div className="al-table-wrap">
        <table className="al-table">
          <thead>
            <tr>
              <th>Datum/tijd</th>
              <th>Account</th>
              <th>Actie</th>
              <th>Onderdeel</th>
              <th>Wijziging</th>
              <th className="al-col-action">Terugdraaien</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="al-empty">Laden…</td></tr>
            )}
            {!loading && entries.length === 0 && (
              <tr><td colSpan={6} className="al-empty"><ScrollText size={26} /><span>Geen logboekvermeldingen</span></td></tr>
            )}
            {!loading && entries.map((e) => (
              <tr key={e.id}>
                <td className="al-date">{formatDateTime(e.created_at)}</td>
                <td className="al-user">{e.user_email || '—'}</td>
                <td><ActionBadge action={e.action} /></td>
                <td className="al-entity">
                  <span className="al-entity-type">{e.entity_type}</span>
                  {e.entity_label && <span className="al-entity-label">{e.entity_label}</span>}
                </td>
                <td className="al-change-cell"><ChangeSummary entry={e} /></td>
                <td className="al-col-action">
                  {e.reverted ? (
                    <span className="al-reverted-badge"><Check size={13} /> Teruggedraaid</span>
                  ) : (
                    <button
                      className="al-revert-btn"
                      onClick={() => { setRevertError(''); setConfirm(e); }}
                    >
                      <Undo2 size={14} /> Terugdraaien
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {toast && <div className="al-toast"><Check size={16} /> {toast}</div>}

      {confirm && (
        <div className="al-modal-overlay" onClick={() => !reverting && setConfirm(null)}>
          <div className="al-modal" onClick={(ev) => ev.stopPropagation()}>
            <div className="al-modal-head">
              <h3><AlertTriangle size={18} /> Wijziging terugdraaien</h3>
              <button className="al-modal-close" onClick={() => setConfirm(null)} disabled={reverting}>
                <X size={18} />
              </button>
            </div>
            <p className="al-modal-text">
              Je staat op het punt de volgende wijziging ongedaan te maken op{' '}
              <strong>{confirm.entity_type}{confirm.entity_label ? ` — ${confirm.entity_label}` : ''}</strong>.
            </p>
            <div className="al-modal-diff">
              <div className="al-modal-diff-head">Dit wordt teruggezet:</div>
              <ChangeSummary entry={confirm} reverse maxFields={20} />
            </div>
            {revertError && <div className="al-modal-error">{revertError}</div>}
            <div className="al-modal-actions">
              <button className="al-btn secondary" onClick={() => setConfirm(null)} disabled={reverting}>
                Annuleren
              </button>
              <button className="al-btn primary" onClick={doRevert} disabled={reverting}>
                {reverting ? 'Bezig…' : 'Ja, terugdraaien'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
